import { useEffect, useState } from 'react';
import { notify } from '@/lib/notify';
import { Link } from 'react-router-dom';
import {
  adminListAllPlans, adminUpdatePlan, type SubscriptionPlan,
} from '@/lib/catalog';
import { humanize } from '@/lib/errors';

/**
 * /admin/pricing — DB-backed catalog editor.
 *
 * Each plan row is editable; saving calls admin_update_subscription_plan
 * which audit-logs old/new diff with PLATFORM_PLAN_UPDATED.
 */
export default function PricingCatalog() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true); setError(null);
    try { setPlans(await adminListAllPlans()); }
    catch (e) { setError(humanize(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  if (loading) return <p className="text-sm text-muted-foreground">Loading catalog…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">Pricing catalog</h2>
        <p className="text-sm text-muted-foreground">
          Edit list prices and tier copy. Changes apply immediately to{' '}
          <Link to="/pricing" className="underline">/pricing</Link> and the in-app /billing comparison.
          Existing active subscriptions keep whatever rate was in effect when they were comped — historical accounting is preserved by the audit log.
        </p>
      </header>

      <div className="space-y-4">
        {plans.map((plan) => (
          <PlanEditor key={plan.id} plan={plan} onSaved={refresh} />
        ))}
      </div>
    </div>
  );
}

function PlanEditor({ plan, onSaved }: { plan: SubscriptionPlan; onSaved: () => void }) {
  const [name, setName] = useState(plan.name);
  const [blurb, setBlurb] = useState(plan.blurb);
  const [monthly, setMonthly] = useState<string>(plan.monthly_cad?.toString() ?? '');
  const [annual, setAnnual] = useState<string>(plan.annual_cad?.toString() ?? '');
  const [cta, setCta] = useState<'start_trial'|'contact_sales'>(plan.cta);
  const [limits, setLimits] = useState(plan.limits_blurb ?? '');
  const [isPublished, setIsPublished] = useState(plan.is_published);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  function parseNum(v: string): number | null {
    if (v.trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  async function save() {
    setBusy(true); setErr(null); setSaved(null);
    try {
      await adminUpdatePlan({
        tier: plan.tier,
        name, blurb,
        monthlyCAD: parseNum(monthly),
        annualCAD: parseNum(annual),
        cta,
        limitsBlurb: limits || null,
        isPublished,
      });
      setSaved('Saved.');
      onSaved();
      notify.success('Plan updated');
    } catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-medium" style={{ color: 'hsl(var(--brand-navy))' }}>
          {plan.tier[0].toUpperCase() + plan.tier.slice(1)} ({plan.tier})
        </h3>
        <span className="text-xs text-muted-foreground">
          Stripe IDs: {plan.stripe_price_id_monthly ?? 'not set'} / {plan.stripe_price_id_annual ?? 'not set'}
        </span>
      </header>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Display name">
          <input value={name} onChange={(e)=>setName(e.target.value)} className="input" />
        </Field>
        <Field label="CTA">
          <select value={cta} onChange={(e)=>setCta(e.target.value as 'start_trial' | 'contact_sales')} className="input">
            <option value="start_trial">Start trial</option>
            <option value="contact_sales">Contact sales</option>
          </select>
        </Field>
        <Field label="Monthly CAD (blank = contact sales)">
          <input type="number" min={0} step="0.01" value={monthly} onChange={(e)=>setMonthly(e.target.value)} className="input" />
        </Field>
        <Field label="Annual CAD per-month equivalent">
          <input type="number" min={0} step="0.01" value={annual} onChange={(e)=>setAnnual(e.target.value)} className="input" />
        </Field>
      </div>

      <Field label="Blurb">
        <textarea value={blurb} onChange={(e)=>setBlurb(e.target.value)} rows={2} className="input" />
      </Field>
      <Field label="Limits / footnote">
        <textarea value={limits} onChange={(e)=>setLimits(e.target.value)} rows={2} className="input" />
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={isPublished} onChange={(e)=>setIsPublished(e.target.checked)} />
        <span>Published (visible on /pricing)</span>
      </label>

      {err && <p className="text-xs text-red-600">{err}</p>}
      {saved && <p className="text-xs text-green-700">{saved}</p>}
      <button disabled={busy} onClick={save} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
        {busy ? 'Saving…' : 'Save changes'}
      </button>

      <style>{`.input{width:100%;border:1px solid hsl(var(--border));border-radius:6px;padding:0.375rem 0.5rem;font-size:0.875rem;background:transparent}`}</style>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs space-y-1">
      <span className="text-muted-foreground uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}
